import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'

import Style from './ThreatFeedDetections.module.css'
import ConfirmPopup from '../../common/popover/confirmPopup'
import { eventReceived, markFalsePositive, clearIncident, loadEvents } from '../../../store/entities/events'
import { unverifyEventsThreatIntel, unSetThreatIntelStatusCode } from '../../../store/entities/threatintel'
import { confirmClear, confirmFalsePositive, confirmUnverify, isGlobalView } from '../../../utilities/config'
import FPTypes from '../../../utilities/falsePositiveTypes'

// ===========================================================================
// Threat Feed — Update Status dropdown (PIXMC-6500).
//
// Mockup-styled <details> dropdown (claude-dashboards), but the actions are exactly the
// existing threat-feed behaviour: Verify navigates to the threat-intel verify flow;
// Unverify / Mark False Positive / Clear go through the existing ConfirmPopup + the shared
// events/threatintel thunks (which optimistically update the shared events.list the table
// reads). This is a NEW component so the legacy UpdatePopover / incidents screens are
// untouched; only the UI is isolated, the Redux state is shared.
//
// Per-status options (PixmAdmin) match the signed-off mapping; non-admins get Mark False
// Positive only, as today.
// ===========================================================================

const ADMIN_OPTIONS = (status) => {
  switch (status) {
    case 'AI Suspicious': return ['verify', 'falsePositive', 'clear']
    case 'PIXM Verified': return ['unverify', 'clear']
    case 'Reported Phish': return ['verify', 'clear']
    case 'Verified Phish':
    case 'Google Form Password': return ['clear']
    default: return ['clear']
  }
}

const LABEL = { verify: 'Verify', unverify: 'Unverify', falsePositive: 'Mark False Positive', clear: 'Clear' }

// Unverify goes through the threatintel slice (not events.statusCode), so its result is shown
// here via threatintel.verifyStatusCode — same copy as the legacy UpdatePopover.
const UNVERIFY_SUCCESS = {
  icon: '', type: 'success',
  title: 'Event removed from Threat Intelligence',
  description: 'The event has been submitted for unverification and will be removed from the Threat Intelligence Feed soon.',
  buttons: 'O',
}
const UNVERIFY_ERROR = {
  icon: '', type: 'error',
  title: 'Event could not be removed from Threat Intelligence',
  description: 'Oops! Something went wrong. Please try again later.',
  buttons: 'O',
}

class ThreatFeedStatusMenu extends Component {
  state = { confirm: null, awaitingUnverify: false }
  detailsRef = React.createRef()

  componentDidMount() {
    document.addEventListener('mousedown', this.handleOutsideClick)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleOutsideClick)
  }

  // Native <details> does not close on an outside click — do it ourselves.
  handleOutsideClick = (e) => {
    const el = this.detailsRef.current
    if (el && el.open && !el.contains(e.target)) el.open = false
  }

  customerId = () => this.props.event.customerId || this.props.selectedCustomer.id

  closeMenu = () => {
    if (this.detailsRef.current) this.detailsRef.current.open = false
  }

  open = (confirm) => {
    this.closeMenu()
    this.setState({ confirm })
  }

  handleVerify = () => {
    this.closeMenu()
    const { event, selectedCustomer, location } = this.props
    this.props.eventReceived({ response: event })
    // Pass the current path as `from` so AddThreatIntel.redirectToThreatFeed() returns the
    // admin here (the Threat Feed tab) after Submit/Cancel — not to the legacy /incidents.
    const pathname = isGlobalView(selectedCustomer.organizationName)
      ? `/tenants/${selectedCustomer.id}/${event.customerId}/threat-intel/${event.id}`
      : `/customers/${selectedCustomer.id}/threat-intel/${event.id}`
    this.props.history.push({ pathname, state: location && location.pathname ? { from: location.pathname } : {} })
  }

  doClear = () => {
    if (this.props.onAction) this.props.onAction('Event cleared successfully.')
    this.props.clearIncident({ eventId: this.props.event.id }, this.customerId())
    this.setState({ confirm: null })
  }
  doFalsePositive = () => {
    const { event } = this.props
    if (this.props.onAction) this.props.onAction('Event marked as false positive.')
    this.props.markFalsePositive(
      [{ eventId: event.id, url: event.url, falsePositiveType: FPTypes.DEFAULT_FP }],
      this.customerId(),
    )
    this.setState({ confirm: null })
  }
  doUnverify = () => {
    this.setState({ confirm: null, awaitingUnverify: true })
    this.props.unverifyEventsThreatIntel(this.props.event.id, this.customerId())
  }
  closeUnverify = () => {
    // Unverify runs through the threatintel slice, which does NOT touch the shared events.list
    // (unlike Clear / Mark False Positive). Re-fetch the current page on success so the actioned
    // row reflects its new status instead of lingering as "PIXM Verified" until a manual reload.
    const { selectedCustomer, threatFilters, verifyStatusCode } = this.props
    const wasSuccess = verifyStatusCode === 200
    this.props.unSetThreatIntelStatusCode()
    this.setState({ awaitingUnverify: false })
    if (wasSuccess && selectedCustomer) {
      this.props.loadEvents(
        threatFilters,
        selectedCustomer.id,
        selectedCustomer.defaultCustomerId,
        isGlobalView(selectedCustomer.organizationName),
      )
    }
  }
  cancel = () => {
    this.setState({ confirm: null })
    this.props.unSetThreatIntelStatusCode()
  }

  render() {
    const { event, tokenRoles } = this.props
    const opts = tokenRoles === 'PixmAdmin' ? ADMIN_OPTIONS(event.status) : ['falsePositive']
    const handler = {
      verify: this.handleVerify,
      unverify: () => this.open('unverify'),
      falsePositive: () => this.open('falsePositive'),
      clear: () => this.open('clear'),
    }

    return (
      <>
        <details className={Style.statusDropdown} ref={this.detailsRef}>
          <summary>Update Status</summary>
          <div className={Style.statusMenu}>
            {opts.map((o) => (
              <button
                key={o}
                className={`${Style.statusMenuItem} ${o === 'clear' ? Style.statusMenuItemDanger : ''}`}
                onClick={handler[o]}
              >
                {LABEL[o]}
              </button>
            ))}
          </div>
        </details>
        {this.state.confirm === 'clear' && (
          <ConfirmPopup details={confirmClear} onYesClick={this.doClear} onCancelClick={this.cancel} />
        )}
        {this.state.confirm === 'falsePositive' && (
          <ConfirmPopup details={confirmFalsePositive} onYesClick={this.doFalsePositive} onCancelClick={this.cancel} />
        )}
        {this.state.confirm === 'unverify' && (
          <ConfirmPopup details={confirmUnverify} onYesClick={this.doUnverify} onCancelClick={this.cancel} />
        )}
        {this.state.awaitingUnverify && this.props.verifyStatusCode ? (
          <ConfirmPopup
            details={this.props.verifyStatusCode === 200 ? UNVERIFY_SUCCESS : UNVERIFY_ERROR}
            onCloseClick={this.closeUnverify}
          />
        ) : null}
      </>
    )
  }
}

const mapStateToProps = (state) => ({
  tokenRoles: state.entities.user.roles,
  selectedCustomer: state.entities.customers.selected,
  threatFilters: state.entities.events.threat.filters,
  verifyStatusCode: state.entities.threatintel.verifyStatusCode,
})

const mapDispatchToProps = (dispatch) => ({
  eventReceived: (data) => dispatch(eventReceived(data)),
  clearIncident: (body, customerId) => dispatch(clearIncident(body, customerId)),
  markFalsePositive: (body, customerId) => dispatch(markFalsePositive(body, customerId)),
  unverifyEventsThreatIntel: (id, customerId) => dispatch(unverifyEventsThreatIntel(id, customerId)),
  unSetThreatIntelStatusCode: () => dispatch(unSetThreatIntelStatusCode()),
  loadEvents: (filters, selectedId, defaultCustomerId, isGlobalView) =>
    dispatch(loadEvents(filters, selectedId, defaultCustomerId, isGlobalView)),
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ThreatFeedStatusMenu))
